import { fetchData, setHeaderOptions } from "#/api/apiHelpers";

export type ArticleOutline = {
  id: string;
  title?: string | null;
  name?: string | null;
  diagnosis_term?: string | null;
  bullets?: string[] | null;
  outline?: string | null;
  content?: string | null;
  body?: string | null;
  status?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
} & Record<string, unknown>;

export type CreateArticleOutlineRequest = {
  title?: string;
  outline?: string;
};

export type UpdateArticleOutlineRequest = CreateArticleOutlineRequest;

const BASE_ROUTE = "/article-outline";
const GENERATE_LIST_ROUTE = "/article/generate/list";
const OUTLINE_STAGE_RUN_ROUTE = "/internal/jobs/article-outline/stage/run";
const MEDICAL_CONCEPT_LIST_ROUTE = "/medical-concept/list";
const OUTLINE_BACKLOG_ROUTE = "/article-outlines/backlog";

export type ArticleOutlinePipelineStage = "outline" | "draft" | "review";

export type StageRunResponse = {
  scanned: number;
  selected: number;
  skipped: number;
  results: Array<Record<string, unknown>>;
};

export type MedicalConceptListItem = {
  id: string;
  name: string;
  description?: string | null;
  concept_type?: string | null;
};

export async function getMedicalConceptById(
  id: string,
  options?: { signal?: AbortSignal }
): Promise<MedicalConceptListItem> {
  if (!id) {
    throw new Error("Medical concept id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<MedicalConceptListItem>(
    `/medical-concept/${encodeURIComponent(id)}`,
    {
      ...request,
      signal: options?.signal,
    }
  );
}

export type MedicalConceptArticleSummary = {
  id: string;
  slug?: string | null;
  title?: string | null;
  version?: number | null;
  created_at?: string;
  updated_at?: string;
};

export type ExistingRelatedConcept = {
  relationshipId: string;
  relationshipType: string;
  direction: "incoming" | "outgoing";
  relatedConceptId: string;
  relatedConceptName: string | null;
  confidence?: number | null;
  strength?: number | null;
  isBidirectional?: boolean | null;
};

export type RelatedConceptSuggestion = {
  name: string;
  description?: string;
  concept_type: string;
  relationship_type: string;
  semantic_type_ids?: string[];
  reason?: string;
  exists?: boolean;
  similarExisting?: Array<{ id: string; name: string; similarity: number }>;
};

export async function listArticleOutlines(
  options?: { signal?: AbortSignal }
): Promise<ArticleOutline[]> {
  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleOutline[]>(BASE_ROUTE, {
    ...request,
    signal: options?.signal,
  });
}

export async function getArticleOutlineById(
  id: string,
  options?: { signal?: AbortSignal }
): Promise<ArticleOutline> {
  if (!id) {
    throw new Error("Article outline id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleOutline>(`${BASE_ROUTE}/${encodeURIComponent(id)}`, {
    ...request,
    signal: options?.signal,
  });
}

export async function createArticleOutline(
  payload: CreateArticleOutlineRequest
): Promise<ArticleOutline> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });
  return fetchData<ArticleOutline>(BASE_ROUTE, request);
}

export async function updateArticleOutline(
  id: string,
  payload: UpdateArticleOutlineRequest
): Promise<ArticleOutline> {
  if (!id) {
    throw new Error("Article outline id is required");
  }

  const request = setHeaderOptions({
    method: "PUT",
    body: payload,
  });
  return fetchData<ArticleOutline>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    request
  );
}

export async function deleteArticleOutline(id: string): Promise<{ id: string }> {
  if (!id) {
    throw new Error("Article outline id is required");
  }

  const request = setHeaderOptions({ method: "DELETE" });
  return fetchData<{ id: string }>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    request
  );
}

export async function approveArticleOutline(
  id: string,
  options?: { generateNow?: boolean; additionalInstructions?: string }
): Promise<{ outline: ArticleOutline; jobId: string }> {
  if (!id) {
    throw new Error("Article outline id is required");
  }

  const body: Record<string, unknown> = {};
  if (options?.generateNow) {
    body.generateNow = true;
  }
  if (options?.additionalInstructions) {
    body.additionalInstructions = options.additionalInstructions;
  }

  const request = setHeaderOptions({
    method: "POST",
    ...(Object.keys(body).length > 0 ? { body } : {}),
  });

  return fetchData<{ outline: ArticleOutline; jobId: string }>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}/approve`,
    request
  );
}

export async function generateArticleOutlineList(
  term: string
): Promise<ArticleOutline[]> {
  if (!term?.trim()) {
    throw new Error("Term is required");
  }

  const request = setHeaderOptions({
    method: "POST",
    body: { term },
  });

  return fetchData<ArticleOutline[]>(GENERATE_LIST_ROUTE, request);
}

export async function runArticleOutlineStage(payload: {
  stage: ArticleOutlinePipelineStage;
  limit: number;
  force?: boolean;
}): Promise<StageRunResponse> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<StageRunResponse>(OUTLINE_STAGE_RUN_ROUTE, request);
}

export async function listMedicalConceptsForOutlines(options?: {
  query?: string;
  limit?: number;
}): Promise<MedicalConceptListItem[]> {
  const request = setHeaderOptions({ method: "GET" });
  const params = new URLSearchParams();
  if (options?.query?.trim()) params.set("query", options.query.trim());
  if (typeof options?.limit === "number") {
    params.set("limit", String(options.limit));
  }

  const route = params.toString()
    ? `${MEDICAL_CONCEPT_LIST_ROUTE}?${params.toString()}`
    : MEDICAL_CONCEPT_LIST_ROUTE;

  return fetchData<MedicalConceptListItem[]>(route, request);
}

export type OutlineBacklogCreateResponse = {
  item?: {
    id?: string;
  };
  duplicates?: Array<Record<string, unknown>>;
  suggestions?: Array<Record<string, unknown>>;
};

export async function createOutlineBacklogFromConcept(payload: {
  conceptId: string;
  topicText: string;
  suggestedTitle?: string;
  priority?: number;
  source?: string;
}): Promise<OutlineBacklogCreateResponse> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<OutlineBacklogCreateResponse>(OUTLINE_BACKLOG_ROUTE, request);
}

export async function triggerOutlineBacklogById(
  id: string
): Promise<StageRunResponse> {
  if (!id) {
    throw new Error("Backlog id is required");
  }

  const request = setHeaderOptions({ method: "POST" });
  return fetchData<StageRunResponse>(
    `${OUTLINE_BACKLOG_ROUTE}/${encodeURIComponent(id)}/trigger-outline`,
    request
  );
}

export async function suggestRelatedMedicalConcepts(payload: {
  seedConceptId: string;
  limit: number;
}): Promise<{
  seedConcept: { id: string; name: string };
  suggestions: RelatedConceptSuggestion[];
}> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<{
    seedConcept: { id: string; name: string };
    suggestions: RelatedConceptSuggestion[];
  }>("/medical-concept/suggest-related", request);
}

export async function listExistingRelatedMedicalConcepts(
  conceptId: string
): Promise<ExistingRelatedConcept[]> {
  if (!conceptId) {
    throw new Error("Medical concept id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ExistingRelatedConcept[]>(
    `/medical-concept/${encodeURIComponent(conceptId)}/related`,
    request
  );
}

export async function listMedicalConceptOutlines(
  conceptId: string
): Promise<ArticleOutline[]> {
  if (!conceptId) {
    throw new Error("Medical concept id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleOutline[]>(
    `/medical-concept/${encodeURIComponent(conceptId)}/outlines`,
    request
  );
}

export async function listMedicalConceptArticles(
  conceptId: string
): Promise<MedicalConceptArticleSummary[]> {
  if (!conceptId) {
    throw new Error("Medical concept id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<MedicalConceptArticleSummary[]>(
    `/medical-concept/${encodeURIComponent(conceptId)}/articles`,
    request
  );
}

export async function materializeRelatedMedicalConcepts(payload: {
  seedConceptId: string;
  items: RelatedConceptSuggestion[];
}): Promise<{
  seedConcept: { id: string; name: string };
  results: Array<{ name: string; conceptId: string; status: "created" | "existing" }>;
}> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<{
    seedConcept: { id: string; name: string };
    results: Array<{ name: string; conceptId: string; status: "created" | "existing" }>;
  }>("/medical-concept/materialize-related", request);
}

export async function regenerateOutlineForMedicalConcept(
  conceptId: string
): Promise<{ outlineId: string; status: string; conceptId: string }> {
  if (!conceptId) {
    throw new Error("Medical concept id is required");
  }

  const request = setHeaderOptions({ method: "POST" });
  return fetchData<{ outlineId: string; status: string; conceptId: string }>(
    `/medical-concept/${encodeURIComponent(conceptId)}/regenerate-outline`,
    request
  );
}

export async function createMedicalConcept(payload: {
  name: string;
  concept_type:
    | "disease"
    | "symptom"
    | "treatment"
    | "pathophysiology"
    | "diagnosis"
    | "procedure"
    | "medication"
    | "anatomy"
    | "biomarker"
    | "risk_factor";
  description?: string | null;
  is_active?: boolean;
  icd10_code?: string | null;
}): Promise<MedicalConceptListItem> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<MedicalConceptListItem>("/medical-concept", request);
}

export async function regenerateArticleOutline(
  id: string
): Promise<{ data: ArticleOutline; error: null }> {
  const request = setHeaderOptions({ method: "POST" });
  return fetchData<{ data: ArticleOutline; error: null }>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}/regenerate`,
    request
  );
}
